import { SingleDropdown, type SingleDropdownOption } from "@/components/molecules/SingleDropdown/SingleDropdown";
import { weatherParameterFieldDisplayName } from "@/i18n/weather-parameter-fields";
import type { LayerKind } from "@/layers/utility/createLayerObject";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components";
import { ParameterFormatForm } from "@/overlay/components/WeatherParameterForm/ParameterFormatForm";
import { FormField, type FormFieldProps } from "@/overlay/components/form-field";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { CrossModelParameterSpecification } from "@mm/api-layers.meteomatics.com";
import type { HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import * as weatherParameter from "@mm/weather-parameter-utils";
import { useMemo } from "react";
import { weatherParamOptionLayersIdxToFilter } from "./optionFilter";
import styles from "./style.module.scss";

export type ParameterChangeCallback = (_: HasLayerIdxNarrowing<PartialWeatherParameter>) => void;
export type ParameterEditCallback = () => void;
export type ParameterRemoveCallback = () => void;

export interface WeatherParameterFormProps {
  parameter: PartialWeatherParameter;
  hideFormatSelector?: boolean;
  /**
   * A string of layer description used to hide particular options that are unavalable.
   */
  layerKind?: LayerKind;
  onChange: ParameterChangeCallback;
  /**
   * Weather to prefill all fields with a default value, even if the field was not yet narrowed by the user.
   */
  autofill?: boolean;
  withTitle?: boolean;
  showExactParameter: boolean;
  /**
   * A callback for when the user clicks on the parameter edit button. If given, it will show a parameter edit button.
   */
  onEditParameter?: ParameterEditCallback;
  onSubmitParam?: (param: HasLayerIdxNarrowing<PartialWeatherParameter>) => void;
  /**
   * A callback for when the user clock on the parameter remove button.  If given, it will show a parameter remove button.
   */
  onRemoveParameter?: ParameterRemoveCallback;
  parameterFormFieldProps?: Partial<FormFieldProps>;
}

/**
 * A form to allow a user select the weather parameter's option.
 */
export function WeatherParameterForm(props: WeatherParameterFormProps) {
  const locale = useLingui().i18n.locale;
  const descriptionField: keyof CrossModelParameterSpecification = locale.startsWith("de")
    ? "description_de"
    : "description_en";
  const desktop = useDesktop();

  const parameter: HasLayerIdxNarrowing<PartialWeatherParameter> = useMemo(() => {
    return weatherParameter.autofillLayerSelection(props.parameter);
  }, [props.parameter]);
  const filteredParameterLayerIdx = weatherParamOptionLayersIdxToFilter(parameter, props.layerKind);

  const parameterStr: string = useMemo(() => {
    return weatherParameter.parameterToString(parameter)?.formatted;
  }, [parameter]);

  function changeLayer(idx: number) {
    const baseParam: HasLayerIdxNarrowing<PartialWeatherParameter> = weatherParameter.copy(parameter);
    baseParam.narrowed_selection.layerIdx = idx;
    weatherParameter.removeInvalidNarrowing(baseParam);
    const newParam = weatherParameter.autofill(baseParam);
    // newParam.narrowed_selection.fields = {};
    // TODO: values are preserved if the layer is changed by design of the PartialWeatherParameter description,
    // but we have to verify that the currently chosen value is still valid in the new layer. at least for non range parameters
    props.onChange(newParam);
  }

  function updateParameter(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    weatherParameter.removeInvalidNarrowing(param);
    props.onChange(param);
  }
  function openSimpleParamWindow() {
    desktop.dispatch({
      type: "setWindowVisibility",
      windowId: instanceId(WindowKind.AddParameter),
      isVisible: true,
    });
    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId: instanceId(WindowKind.AddParameter),
      windowKind: WindowKind.AddParameter,
      customProps: {
        source: "edit-param",
        onSubmitParam: props.onSubmitParam,
      },
    });
  }
  const layerOptions = useMemo<SingleDropdownOption<string>[]>(
    () =>
      parameter.parameter.layers.flatMap((layer, layerIdx) =>
        filteredParameterLayerIdx.includes(layerIdx) ? [] : [{ value: String(layerIdx), label: layer.format }],
      ),
    [parameter.parameter.layers, filteredParameterLayerIdx],
  );

  return (
    <>
      {props.withTitle ? <h3>{props.parameter.parameter[descriptionField]}</h3> : null}
      {/* Parameter display */}
      {props.showExactParameter && parameterStr ? (
        <FormField label={t`Parameter`} {...props.parameterFormFieldProps}>
          <div className={"editable-variable"}>
            <span
              className={styles["parameter-name-span"]}
              // Title attribute shows the full parameter name in case it's too long and cut off at the end.
              title={parameterStr}
            >
              {parameterStr}
            </span>
            <div className={"editable-variable__actions"}>
              {props.onEditParameter || props.onSubmitParam ? (
                <span
                  onClick={props.onEditParameter || openSimpleParamWindow}
                  className="material-icons editable-variable__edit"
                >
                  edit
                </span>
              ) : null}
              {props.onRemoveParameter && (
                <span
                  onClick={props.onRemoveParameter}
                  className={classNames("material-icons editable-variable__edit")}
                >
                  delete
                </span>
              )}
            </div>
          </div>
        </FormField>
      ) : null}
      {/* Parameter format selector */}
      {props.hideFormatSelector || parameter.parameter.layers.length <= 1 ? null : (
        <FormField label={weatherParameterFieldDisplayName.format()}>
          <SingleDropdown
            options={layerOptions}
            value={String(parameter.narrowed_selection.layerIdx)}
            onChange={(value) => changeLayer(Number(value))}
          />
        </FormField>
      )}
      {/* Parameter format details */}
      {!props.hideFormatSelector && (
        <ParameterFormatForm layerKind={props.layerKind} parameter={parameter} onChange={updateParameter} />
      )}
    </>
  );
}
